import { Spinner } from "components/DesignSystem/Loader/loader";
import { useToast } from "hooks/useToast";
import {
  useBulkUploadDocumentsMutation,
  useGetEntityDocumentTypesQuery,
  useGetEntityOverviewQuery,
  useGetRecentDocumentsQuery,
} from "v2/store/entityDocumentsApi";
import { CompanyRecordsCard } from "./CompanyRecordsCard";
import { ExtractedReviewBanner } from "./ExtractedReviewBanner";
import { RecentlyUpdatedByInkle } from "./RecentlyUpdatedByInkle";
import { RecentlyUploadedByYou } from "./RecentlyUploadedByYou";
import { RequiredFromYou } from "./RequiredFromYou";

type OverviewTabProps = {
  entityId: string;
};

const readErrorMessage = (error: unknown): string =>
  (error as { data?: { message?: string } })?.data?.message ??
  "Something went wrong";

export const OverviewTab = ({ entityId }: OverviewTabProps) => {
  const { alertToast } = useToast();

  const { data: overview, isLoading: isOverviewLoading } =
    useGetEntityOverviewQuery({ entityId });
  const { data: requiredItems = [], isLoading: isTypesLoading } =
    useGetEntityDocumentTypesQuery({ entityId });
  const { data: uploadedByYou = [] } = useGetRecentDocumentsQuery({
    entityId,
    by: "you",
  });
  const { data: uploadedByInkle = [] } = useGetRecentDocumentsQuery({
    entityId,
    by: "inkle",
  });

  const [bulkUpload] = useBulkUploadDocumentsMutation();

  if (isOverviewLoading || isTypesLoading) {
    return (
      <div className="t-flex t-justify-center t-py-16">
        <Spinner size="regular" />
      </div>
    );
  }

  const handleUploadFiles = async (files: File[]): Promise<string[]> => {
    try {
      const uploaded = await bulkUpload({ entityId, files }).unwrap();
      return uploaded.map((doc) => doc.uuid);
    } catch (error) {
      alertToast({ message: readErrorMessage(error) });
      throw error;
    }
  };

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <CompanyRecordsCard completeness={overview?.completeness ?? 0} />
      <RequiredFromYou
        entityId={entityId}
        items={requiredItems}
        onUploadFiles={handleUploadFiles}
      />
      <ExtractedReviewBanner entityId={entityId} />
      <RecentlyUploadedByYou documents={uploadedByYou} />
      <RecentlyUpdatedByInkle documents={uploadedByInkle} />
    </div>
  );
};
